import { useMutation, useQuery } from "@tanstack/solid-query";
import type { CurrentRunResponse } from "contracts/api/crawl/run-read.responses";
import { isActiveCrawlRunStatus } from "contracts/enums/core";
import { createEffect } from "solid-js";
import { api } from "../../store/api";
import { RUN_IDLE_POLL_MS, RUN_POLL_MS } from "../../store/pollCadence";
import { queryClient } from "../../store/query";

const currentRunQueryKey = (websiteId: string) =>
  ["current-run", websiteId] as const;

/**
 * Cancels a running scan via the operator abort endpoint. Not an outbox
 * write: a failed abort surfaces immediately. Every run surface is
 * invalidated on settle so the failed state shows up without waiting for the
 * next poll.
 */
export function createAbortRun(websiteId: string) {
  return useMutation(() => ({
    networkMode: "online" as const,
    retry: 1,
    mutationFn: (runId: string) => api.abortRun(websiteId, runId),
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: currentRunQueryKey(websiteId),
      });
      void queryClient.invalidateQueries({ queryKey: ["runs", websiteId] });
      void queryClient.invalidateQueries({
        queryKey: ["run-detail", websiteId],
      });
    },
  }));
}

/**
 * Re-analyses selected pages of a landed run. The run keeps its id, so every
 * run surface is invalidated in place — there is nothing to navigate to.
 */
export function createRunRepair(websiteId: string) {
  return useMutation(() => ({
    networkMode: "online" as const,
    retry: 1,
    mutationFn: (input: { runId: string; urls: string[] }) =>
      api.repairRun(websiteId, input.runId, { urls: input.urls }),
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: currentRunQueryKey(websiteId),
      });
      void queryClient.invalidateQueries({ queryKey: ["runs", websiteId] });
      void queryClient.invalidateQueries({
        queryKey: ["run-detail", websiteId],
      });
      void queryClient.invalidateQueries({
        queryKey: ["overview", websiteId],
      });
    },
  }));
}

/**
 * Polls the active run for a website and stops once it fully lands (terminal
 * status AND analysis written — the backend returns null then). On the
 * active→landed transition every scan surface is stale, so the related query
 * keys are invalidated here; mount once per scan shell (ScanTopBar).
 */
export function createRunState(websiteId: string) {
  const query = useQuery<CurrentRunResponse>(() => ({
    queryKey: currentRunQueryKey(websiteId),
    queryFn: () => api.currentRun(websiteId),
    // Fast poll while a run is active; slow idle poll otherwise so a scan
    // started by another client (or tab) still shows up here.
    refetchInterval: (current) => {
      const run = current.state.data?.run;
      return run && isActiveCrawlRunStatus(run.status)
        ? RUN_POLL_MS
        : RUN_IDLE_POLL_MS;
    },
  }));

  const run = () => query.data?.run ?? null;
  const yeetWorker = () => query.data?.yeetWorker ?? null;

  let wasActive = false;
  createEffect(() => {
    const active = run() !== null;
    if (wasActive && !active) {
      void queryClient.invalidateQueries({ queryKey: ["overview", websiteId] });
      void queryClient.invalidateQueries({ queryKey: ["websites"] });
      void queryClient.invalidateQueries({ queryKey: ["runs", websiteId] });
      void queryClient.invalidateQueries({
        queryKey: ["run-detail", websiteId],
      });
    }
    wasActive = active;
  });

  return { query, run, yeetWorker };
}
